import type { TFunction } from 'i18next';
import type { ServicePriceKey } from '../config/pricing';

export type FeatureType = 'included' | 'excluded';

export interface PlanFeature {
  type: FeatureType;
  text: string;
}

export interface ServiceFaq {
  question: string;
  answer: string;
}

export interface ServicePlan {
  key: ServicePriceKey;
  title: string;
  titleNote?: string;
  desc: string;
  overview: string;
  idealClient: string;
  outcomes: string[];
  whenToUse: string[];
  workflow: string[];
  workflowTitles: string[];
  faq: ServiceFaq[];
  recommended?: boolean;
  recommendedLabel?: string;
  features: PlanFeature[];
  ctaLabel: string;
  ctaClassName: string;
}

const getList = (t: TFunction, key: string) =>
  t(key, { returnObjects: true }) as string[];

const getFaq = (t: TFunction, key: string) =>
  t(key, { returnObjects: true }) as ServiceFaq[];

export const getServicePlans = (t: TFunction): ServicePlan[] => [
  {
    key: 'ecommerce',
    title: t('services.plan_ecommerce_title'),
    desc: t('services.plan_ecommerce_desc'),
    overview: t('services.service_details.ecommerce.overview'),
    idealClient: t('services.ideal_clients.ecommerce'),
    outcomes: getList(t, 'services.service_details.ecommerce.outcomes'),
    whenToUse: getList(t, 'services.service_details.ecommerce.when_to_use'),
    workflow: getList(t, 'services.service_details.ecommerce.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.ecommerce.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.ecommerce.faq'),
    recommended: true,
    recommendedLabel: t('services.plan_recommended_specialty'),
    features: [
      { type: 'included', text: t('services.features.ecommerce_store_setup') },
      {
        type: 'included',
        text: t('services.features.ecommerce_catalog_checkout'),
      },
      { type: 'included', text: t('services.features.ecommerce_integrations') },
      {
        type: 'included',
        text: t('services.features.ecommerce_performance_tracking'),
      },
      { type: 'included', text: t('services.features.delivery_hand_over') },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'pim',
    title: t('services.plan_pim_title'),
    titleNote: t('services.plan_pim_note'),
    desc: t('services.plan_pim_desc'),
    overview: t('services.service_details.pim.overview'),
    idealClient: t('services.ideal_clients.pim'),
    outcomes: getList(t, 'services.service_details.pim.outcomes'),
    whenToUse: getList(t, 'services.service_details.pim.when_to_use'),
    workflow: getList(t, 'services.service_details.pim.workflow'),
    workflowTitles: getList(t, 'services.service_details.pim.workflow_titles'),
    faq: getFaq(t, 'services.service_details.pim.faq'),
    features: [
      { type: 'included', text: t('services.features.pim_integration') },
      { type: 'included', text: t('services.features.pim_data_model') },
      { type: 'included', text: t('services.features.pim_import_export') },
      { type: 'included', text: t('services.features.pim_quality_rules') },
      { type: 'included', text: t('services.features.delivery_hand_over') },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'wordpress',
    title: t('services.plan_wordpress_title'),
    desc: t('services.plan_wordpress_desc'),
    overview: t('services.service_details.wordpress.overview'),
    idealClient: t('services.ideal_clients.wordpress'),
    outcomes: getList(t, 'services.service_details.wordpress.outcomes'),
    whenToUse: getList(t, 'services.service_details.wordpress.when_to_use'),
    workflow: getList(t, 'services.service_details.wordpress.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.wordpress.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.wordpress.faq'),
    features: [
      { type: 'included', text: t('services.features.wp_build_or_rebuild') },
      { type: 'included', text: t('services.features.wp_performance') },
      {
        type: 'included',
        text: t('services.features.wp_security_hardening'),
      },
      { type: 'included', text: t('services.features.wp_plugins_quality') },
      {
        type: 'included',
        text: t('services.features.delivery_monthly_maintenance'),
      },
      { type: 'included', text: t('services.features.wp_technical_seo') },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'tech-seo',
    title: t('services.plan_tech_seo_title'),
    desc: t('services.plan_tech_seo_desc'),
    overview: t('services.service_details.tech_seo.overview'),
    idealClient: t('services.ideal_clients.tech_seo'),
    outcomes: getList(t, 'services.service_details.tech_seo.outcomes'),
    whenToUse: getList(t, 'services.service_details.tech_seo.when_to_use'),
    workflow: getList(t, 'services.service_details.tech_seo.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.tech_seo.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.tech_seo.faq'),
    recommended: true,
    recommendedLabel: t('services.plan_recommended_fast_impact'),
    features: [
      {
        type: 'included',
        text: t('services.features.deliverable_seo_audit'),
      },
      {
        type: 'included',
        text: t('services.features.deliverable_cw_vitals'),
      },
      { type: 'included', text: t('services.features.deliverable_indexing') },
      {
        type: 'included',
        text: t('services.features.delivery_weekly_report'),
      },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'analytics-schema',
    title: t('services.plan_analytics_schema_title'),
    desc: t('services.plan_analytics_schema_desc'),
    overview: t('services.service_details.analytics_schema.overview'),
    idealClient: t('services.ideal_clients.analytics_schema'),
    outcomes: getList(t, 'services.service_details.analytics_schema.outcomes'),
    whenToUse: getList(
      t,
      'services.service_details.analytics_schema.when_to_use',
    ),
    workflow: getList(t, 'services.service_details.analytics_schema.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.analytics_schema.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.analytics_schema.faq'),
    features: [
      { type: 'included', text: t('services.features.ga4_setup') },
      { type: 'included', text: t('services.features.gtm_events') },
      { type: 'included', text: t('services.features.google_shopping_events') },
      { type: 'included', text: t('services.features.schema_markup') },
      { type: 'included', text: t('services.features.analytics_validation') },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'ux',
    title: t('services.plan_ux_title'),
    desc: t('services.plan_ux_desc'),
    overview: t('services.service_details.ux.overview'),
    idealClient: t('services.ideal_clients.ux'),
    outcomes: getList(t, 'services.service_details.ux.outcomes'),
    whenToUse: getList(t, 'services.service_details.ux.when_to_use'),
    workflow: getList(t, 'services.service_details.ux.workflow'),
    workflowTitles: getList(t, 'services.service_details.ux.workflow_titles'),
    faq: getFaq(t, 'services.service_details.ux.faq'),
    features: [
      { type: 'included', text: t('services.features.ux_modern_layout') },
      { type: 'included', text: t('services.features.ux_better_navigation') },
      { type: 'included', text: t('services.features.ux_usability') },
      { type: 'included', text: t('services.features.ux_accessibility') },
      { type: 'included', text: t('services.features.ux_responsive_design') },
      { type: 'included', text: t('services.features.ux_intuitive_interface') },
      { type: 'included', text: t('services.features.ux_light_dark_theme') },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'digital-link-page',
    title: t('services.plan_digital_link_page_title'),
    desc: t('services.plan_digital_link_page_desc'),
    overview: t('services.service_details.digital_link_page.overview'),
    idealClient: t('services.ideal_clients.digital_link_page'),
    outcomes: getList(t, 'services.service_details.digital_link_page.outcomes'),
    whenToUse: getList(
      t,
      'services.service_details.digital_link_page.when_to_use',
    ),
    workflow: getList(t, 'services.service_details.digital_link_page.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.digital_link_page.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.digital_link_page.faq'),
    features: [
      { type: 'included', text: t('services.features.link_page_links') },
      { type: 'included', text: t('services.features.link_page_socials') },
      { type: 'included', text: t('services.features.link_page_contact') },
      { type: 'included', text: t('services.features.link_page_offers') },
      { type: 'included', text: t('services.features.link_page_responsive') },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg btn-gradient',
  },
  {
    key: 'cyber',
    title: t('services.plan_cyber_title'),
    desc: t('services.plan_cyber_desc'),
    overview: t('services.service_details.cyber.overview'),
    idealClient: t('services.ideal_clients.cyber'),
    outcomes: getList(t, 'services.service_details.cyber.outcomes'),
    whenToUse: getList(t, 'services.service_details.cyber.when_to_use'),
    workflow: getList(t, 'services.service_details.cyber.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.cyber.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.cyber.faq'),
    features: [
      {
        type: 'included',
        text: t('services.features.cyber_risk_assessment'),
      },
      { type: 'included', text: t('services.features.cyber_website_security') },
      { type: 'included', text: t('services.features.cyber_api_security') },
      { type: 'included', text: t('services.features.cyber_code_security') },
      { type: 'included', text: t('services.features.cyber_monitoring') },
      {
        type: 'included',
        text: t('services.features.delivery_security_report'),
      },
    ],
    ctaLabel: t('services.cta_request_specialist'),
    ctaClassName: 'btn-lg',
  },
  {
    key: 'audit',
    title: t('services.plan_audit_title'),
    desc: t('services.plan_audit_desc'),
    overview: t('services.service_details.audit.overview'),
    idealClient: t('services.ideal_clients.audit'),
    outcomes: getList(t, 'services.service_details.audit.outcomes'),
    whenToUse: getList(t, 'services.service_details.audit.when_to_use'),
    workflow: getList(t, 'services.service_details.audit.workflow'),
    workflowTitles: getList(
      t,
      'services.service_details.audit.workflow_titles',
    ),
    faq: getFaq(t, 'services.service_details.audit.faq'),
    features: [
      { type: 'included', text: t('services.plan_audit_feature_privacy') },
      {
        type: 'included',
        text: t('services.plan_audit_feature_performance'),
      },
      {
        type: 'included',
        text: t('services.plan_audit_feature_accessibility'),
      },
      {
        type: 'included',
        text: t('services.plan_audit_feature_security'),
      },
    ],
    ctaLabel: t('services.cta_get_started'),
    ctaClassName: 'btn-lg btn-gradient',
  },
];
